import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import { VisitOutcome } from "../../features/sessions/models/VisitOutcome";
import { buildAttendanceRoster } from "../../features/sessions/utils/buildAttendanceRoster";
import {
  selectOccurrenceCancelled,
  selectOccurrenceLoaded,
  selectOccurrenceOneOffMembers,
  selectSessionVisits,
  SessionVisits,
} from "../../features/sessions/visits-slice";
import {
  fetchSessionAttendance,
  saveSessionAttendance,
} from "../../features/sessions/slice-extensions/visits-slice-extension";
import { selectAbsencesForDay } from "../../features/absences/absences-slice";
import { fetchAbsencesForDay } from "../../features/absences/slice-extensions/absences-slice-extension";
import { useAttendanceDirectory } from "../../features/sessions/hooks/useAttendanceDirectory";
import { formatDateDisplay } from "../../features/sessions/utils/DisplayMappers";
import { showErrorNotification } from "../../utils/notifications";
import Button from "../button/Button";
import UnsavedChangesGuard from "../unsaved-changes/UnsavedChangesGuard";
import BulkOutcomeButtons from "./BulkOutcomeButtons";
import OneOffMembersSection from "./OneOffMembersSection";
import RosterAttendanceSection from "./RosterAttendanceSection";
import { useAttendanceFeeNames } from "../../features/sessions/hooks/useAttendanceFeeNames";
import { useAttendanceCounts } from "../../features/sessions/hooks/useAttendanceCounts";
import CancelledAttendance from "./CancelledAttendance";

interface AttendancePanelProps {
  session: Session;
  /** The occurrence date (YYYY-MM-DD) to mark; defaults to the session's own date. */
  occurrenceDate?: string;
  /**
   * Embedded inside an occurrence row (no card chrome, no collapse toggle) —
   * the surrounding row already provides the date context.
   */
  embedded?: boolean;
}

const AttendancePanel: React.FC<AttendancePanelProps> = ({ session, occurrenceDate, embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { members, groups } = useAttendanceDirectory();

  // Attendance is recorded per occurrence; default to the session's own date.
  const date = occurrenceDate ?? session.dateStart.slice(0, 10);
  const saved = useAppSelector(selectSessionVisits(session.id, date));
  const savedOneOffs = useAppSelector(selectOccurrenceOneOffMembers(session.id, date));
  const feeNames = useAttendanceFeeNames(session.id, date);
  const cancelled = useAppSelector(selectOccurrenceCancelled(session.id, date));
  const loaded = useAppSelector(selectOccurrenceLoaded(session.id, date));
  const [loadFailed, setLoadFailed] = useState(false);

  const [outcomes, setOutcomes] = useState<SessionVisits>({});
  const [oneOffIds, setOneOffIds] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // Set once the instructor edits, so freshly loaded data doesn't clobber changes.
  const userEdited = useRef(false);

  // Load any saved attendance for this occurrence; switching dates starts a fresh
  // draft and reopens the panel (the instructor just asked for this date). A failed
  // load falls back to the editable panel, as before.
  useEffect(() => {
    userEdited.current = false;
    setExpanded(true);
    setLoadFailed(false);
    dispatch(fetchSessionAttendance({ bookingId: session.id, occurrenceDate: date })).then((result) => {
      if (fetchSessionAttendance.rejected.match(result)) setLoadFailed(true);
    });
    // Declared absences covering this date drive the display-only "Away" badges.
    dispatch(fetchAbsencesForDay({ date }));
  }, [dispatch, session.id, date]);

  const dayAbsences = useAppSelector(selectAbsencesForDay(date));
  const awayMemberIds = useMemo(
    () => new Set((dayAbsences ?? []).map((absence) => absence.memberId)),
    [dayAbsences],
  );

  const roster = useMemo(
    () => buildAttendanceRoster(session, members, groups),
    [session, members, groups]
  );

  // Seed the working draft when the occurrence, roster, or loaded attendance changes.
  // Default to Attended per the vision's fast path; preserve any saved outcomes. The
  // guard prevents clobbering edits on re-renders.
  const seedKey = useMemo(
    () =>
      `${date}|${[...roster.memberIds].sort().join(",")}|${JSON.stringify(saved)}|${savedOneOffs.join(",")}`,
    [date, roster.memberIds, saved, savedOneOffs]
  );
  const lastSeed = useRef<string | null>(null);
  useEffect(() => {
    if (userEdited.current) return;
    if (lastSeed.current === seedKey) return;
    lastSeed.current = seedKey;
    const next: SessionVisits = {};
    for (const id of roster.memberIds) {
      next[id] = saved[id] ?? VisitOutcome.Attended;
    }
    for (const id of savedOneOffs) {
      next[id] = saved[id] ?? VisitOutcome.Attended;
    }
    setOutcomes(next);
    setOneOffIds(savedOneOffs);
    const unsavedSeat = savedOneOffs.some((id) => saved[id] === undefined);
    setIsDirty(unsavedSeat || (roster.memberIds.length > 0 && Object.keys(saved).length === 0));
  }, [seedKey, roster.memberIds, saved, savedOneOffs, date]);

  const setOne = (memberId: string, outcome: VisitOutcome) => {
    userEdited.current = true;
    setOutcomes((prev) => ({ ...prev, [memberId]: outcome }));
    setIsDirty(true);
    setJustSaved(false);
  };

  const setMany = (memberIds: string[], outcome: VisitOutcome) => {
    userEdited.current = true;
    setOutcomes((prev) => {
      const next = { ...prev };
      for (const id of memberIds) next[id] = outcome;
      return next;
    });
    setIsDirty(true);
    setJustSaved(false);
  };

  const addOneOff = (memberId: string) => {
    userEdited.current = true;
    setOneOffIds((prev) => (prev.includes(memberId) ? prev : [...prev, memberId]));
    setOutcomes((prev) => ({ ...prev, [memberId]: VisitOutcome.Attended }));
    setIsDirty(true);
    setJustSaved(false);
  };

  const removeOneOff = (memberId: string) => {
    userEdited.current = true;
    setOneOffIds((prev) => prev.filter((id) => id !== memberId));
    setOutcomes((prev) => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });
    setIsDirty(true);
    setJustSaved(false);
  };

  const handleSave = async () => {
    // Optimistic: the panel flips to "saved" immediately — the instructor is
    // at the door, not waiting on a spinner. A failed save rolls the draft
    // back to dirty (marks intact) and surfaces a retryable error.
    userEdited.current = false;
    setIsDirty(false);
    setJustSaved(true);
    try {
      await dispatch(
        saveSessionAttendance({
          bookingId: session.id,
          occurrenceDate: date,
          outcomes,
          oneOffMemberIds: oneOffIds,
        })
      ).unwrap();
    } catch {
      userEdited.current = true;
      setIsDirty(true);
      setJustSaved(false);
      showErrorNotification(t("sessions.attendance.saveFailed"));
    }
  };

  const allMemberIds = useMemo(
    () => [...roster.memberIds, ...oneOffIds],
    [roster.memberIds, oneOffIds]
  );

  const counts = useAttendanceCounts(allMemberIds, outcomes);
  const summaryText = t("sessions.attendance.summary", {
    present: counts.present,
    absent: counts.absent,
    unmarked: counts.unmarked,
  });

  // No record action until the date's attendance (and its cancelled state) has arrived.
  if (!loaded && !loadFailed) {
    return <p className="py-2 text-sm text-grey-40 dark:text-grey-60">{t("common.loading")}</p>;
  }

  // An instructor-cancelled date takes no attendance: notice only, recorded Visits read-only.
  if (cancelled) {
    return <CancelledAttendance date={date} outcomes={saved} members={members} embedded={embedded} />;
  }

  return (
    <div className={embedded ? "py-2" : "with-border rounded-2xl p-4 lg:p-6"}>
      {/* Mounted only after a real user edit — a route supports one active blocker,
          and several panels can render on the same page. The auto-seeded
          "everyone attended" draft keeps isDirty for the Save button but must
          not block navigation. */}
      {isDirty && userEdited.current && (
        <UnsavedChangesGuard when={() => isDirty && userEdited.current} />
      )}
      {!embedded && (
        <div className="flex items-center justify-between gap-3">
          <h3>{t("sessions.attendance.title")}</h3>
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="btn btn-secondary"
          >
            {expanded ? (
              <>
                <ChevronUp size={18} />
                <span>{t("sessions.attendance.hide")}</span>
              </>
            ) : (
              <>
                <ChevronDown size={18} />
                <span>{t("sessions.attendance.markAttendance")}</span>
              </>
            )}
          </button>
        </div>
      )}

      <div className={`flex flex-wrap items-center gap-2 text-sm text-grey-30 dark:text-grey-70 ${embedded ? "" : "mt-3"}`}>
        <span className="font-medium text-grey-10 dark:text-grey-90">
          {formatDateDisplay(date)}
        </span>
        <span>{summaryText}</span>
        {justSaved && (
          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
            <Check size={15} />
            {t("sessions.attendance.savedJustNow")}
          </span>
        )}
      </div>

      {(embedded || expanded) && (
        <div className="mt-4 space-y-4">
          <BulkOutcomeButtons memberIds={allMemberIds} onSetMany={setMany} />

          {roster.sections.length === 0 && oneOffIds.length === 0 && (
            <p className="text-sm text-grey-40 dark:text-grey-60">
              {t("sessions.attendance.emptyRoster")}
            </p>
          )}

          {roster.sections.map((section) => (
            <RosterAttendanceSection
              key={section.kind === "group" ? `group-${section.groupId}` : "individuals"}
              section={section}
              outcomes={outcomes}
              feeNames={feeNames}
              awayMemberIds={awayMemberIds}
              onChange={setOne}
              onChangeMany={setMany}
            />
          ))}

          {/* One-off members — attend this occurrence only, billed via their visit. */}
          <OneOffMembersSection
            members={members}
            oneOffIds={oneOffIds}
            outcomes={outcomes}
            feeNames={feeNames}
            excludedMemberIds={allMemberIds}
            onAdd={addOneOff}
            onRemove={removeOneOff}
            onChange={setOne}
          />

          <div className="flex items-center justify-end gap-3">
            {justSaved && (
              <span className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
                <Check size={16} />
                {t("sessions.attendance.saved")}
              </span>
            )}
            <Button onClick={handleSave} disabled={!isDirty}>
              {t("sessions.attendance.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePanel;
